import { useEffect, useState } from "react";
import { Box } from "@mui/material";

export type HighlightSlide = {
  title: string;
  value: string;
  sub?: string;
  accent: string;
  /** Optional emoji/glyph shown as a small badge. */
  icon?: string;
};

type HighlightSliderProps = {
  slides: HighlightSlide[];
  intervalMs?: number;
};

const INK = "#1F2937";
const MUTED = "#6B7280";

/**
 * Auto-rotating "highlights" ticker — one big-number highlight at a time,
 * advancing on a timer (pauses on hover), with clickable dots. Fills whatever
 * height its container gives it.
 */
const HighlightSlider = ({ slides, intervalMs = 4000 }: HighlightSliderProps) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  // Keep index valid if the slide set changes.
  useEffect(() => {
    if (index >= slides.length) setIndex(0);
  }, [slides.length, index]);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = setInterval(
      () => setIndex((p) => (p + 1) % slides.length),
      intervalMs,
    );
    return () => clearInterval(id);
  }, [paused, slides.length, intervalMs]);

  if (!slides.length) return null;
  const s = slides[Math.min(index, slides.length - 1)];

  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        background: "#fff",
        borderRadius: "10px",
        border: "1px solid #F0F0F0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        p: 2,
        height: "100%",
        minHeight: 200,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        // accent side-rail reflects the active slide
        borderLeft: `4px solid ${s.accent}`,
      }}
    >
      <Box
        sx={{
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "0.06em",
          color: MUTED,
          textTransform: "uppercase",
        }}
      >
        Highlights
      </Box>

      {/* animated slide body */}
      <Box
        key={index}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 0.5,
          px: 1,
          "@keyframes hs-in": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "hs-in 420ms ease",
        }}
      >
        {s.icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              mb: 0.5,
              background: `${s.accent}1A`,
            }}
          >
            {s.icon}
          </Box>
        )}
        <Box
          sx={{
            fontSize: { xs: 26, md: 34 },
            fontWeight: 800,
            lineHeight: 1.1,
            color: s.accent,
            wordBreak: "break-word",
          }}
        >
          {s.value}
        </Box>
        <Box sx={{ fontSize: 14, fontWeight: 600, color: INK }}>{s.title}</Box>
        {s.sub && (
          <Box sx={{ fontSize: 12.5, color: MUTED }}>{s.sub}</Box>
        )}
      </Box>

      {/* dots */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 0.75,
          mt: 1,
        }}
      >
        {slides.map((sl, i) => (
          <Box
            key={i}
            onClick={() => setIndex(i)}
            sx={{
              width: i === index ? 16 : 6,
              height: 6,
              borderRadius: 3,
              cursor: "pointer",
              transition: "width 200ms, background 200ms",
              background: i === index ? sl.accent : "#D8DCE0",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default HighlightSlider;
